import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import Vue, { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { calenderConvert } from '@/appLogic/importData/calendar';
import { getOrganizationId } from '@/utils/token';
import { ICalendar, IBizDays, setDefaultSelectFiscalYear, equalYearMonthInfo } from '@/appLogic/interfaces/date';
import { ActiveDate } from '@/coreDomainsModule/table';
import YearCalendar from '@/components/calendar/calendarComponents/YearCalendar.vue';
import Btn, { ButtonStyle } from '@/components/input/Btn/Btn.vue';
import { getCalendar, removeChecker } from '@/appLogic/checkCalendar';
import _ from 'lodash';
import ConfirmModal from '@/components/modal/ConfirmModal.vue';
import binarySearch from '@/utils/algorithm/binarySearch';
import removeRecommendOfBusinesses from '@/appLogic/business/removeRecommendOfBusiness';
import { putPreference } from '@/appLogic/preferenceManage';
import { i18n } from '@/i18n';
import { simpleCopy } from '@/utils/deepCopy';
import styled from 'vue-styled-components';
import FileInput from '@/components/input/FileInput.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';

interface ICalendarData {
  className: string;
  date: string;
  organizationId: string;
  selected: boolean;
}

// TODO: 初期値？''にする必要あるのか？
// ConfirmModalに付けるアクションの型
type ConfirmModalActionsType = 'select' | 'button' | '';

// ConfirmModalのステータスの型
type ConfirmModalState = {
  state: boolean;
  type: ConfirmModalActionsType;
};

interface ICalendarTable {
  message: string;
  lists: IBizDays[];
  selectYear: number;
  activeDates: ActiveDate[];
  activeOrganizationId: string;
  activeCalendar: ICalendar;
  reserveCalendar: { [key: string]: ICalendarData };
  updateStatus: boolean;
  confirmModalState: ConfirmModalState; // typeは'select'か'button'のどちらか
  preRegisDate: ICalendar;
}

// style variables

export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarTable',
  setup(__props) {

const ConfirmModalMain = styled.section`
  font-size: 18px;
  text-align: center;
  padding-bottom: 15px;
`;

// 変数
const t = i18n.t;
const date: Date = new Date();
const inputCsvFile = ref<typeof FileInput | null>(null);

const state: ICalendarTable = reactive<ICalendarTable>({
  message: '',
  lists: [],
  selectYear: setDefaultSelectFiscalYear(date),
  activeDates: [],
  activeOrganizationId: '',
  activeCalendar: { organizationId: '', updatedAt: 0, workDays: [], unRegisterCalendarAlert: { updateAt: 0, isDisplay: true } },
  reserveCalendar: {},
  updateStatus: false,
  confirmModalState: { state: false, type: '' }, // typeは'select'か'button'のどちらか
  preRegisDate: {
    organizationId: '',
    updatedAt: 0,
    workDays: [],
    unRegisterCalendarAlert: { updateAt: 0, isDisplay: true },
  },
}) as ICalendarTable;

// computed
/**
 * 更新ボタンの色切り替え
 */
const buttonStyle = computed((): ButtonStyle => {
  if (state.updateStatus) {
    return 'btn-primary';
  } else {
    return 'btn-default';
  }
});

/**
 * 更新ボタンの選択可否の決定
 */
const buttonDisabled = computed((): boolean => {
  return !state.updateStatus;
});

// イベント
onMounted(async () => {
  await setUpAction();
});

/**
 * カレンダーのセットアップ
 */
const setUpAction = async (): Promise<void> => {
  const calendar = await getCalendar();
  if (calendar) {
    await changeActiveCalendar(calendar.workDays, calendar.organizationId);
    state.activeCalendar = calendar;
  }
  if (inputCsvFile.value) {
    inputCsvFile.value.resetInputFile();
  }
};

/**
 * 稼働日情報の切り替え
 * @param workDays 稼働日
 * @param organizationId 企業ID
 */
const changeActiveCalendar = async (workDays: IBizDays[], organizationId: string): Promise<void> => {
  state.activeOrganizationId = organizationId;
  const activeDateMaps: { [key: string]: { date: string; className: string } } = {}; // year-month-dayで重複する可能性がある？
  for (const workDay of workDays) {
    const month = String(workDay.month).length == 1 ? '0' + String(workDay.month) : String(workDay.month);
    const year = String(workDay.year).length == 1 ? '0' + String(workDay.year) : String(workDay.year);
    for (const day of workDay.days) {
      activeDateMaps[`${year}-${month}-${String(day).length == 1 ? '0' + String(day) : String(day)}`] = {
        date: `${year}-${month}-${String(day).length == 1 ? '0' + String(day) : String(day)}`,
        className: 'red',
      };
    }
  }
  state.activeDates = Object.values(activeDateMaps);
};

/**
 * カレンダー登録モーダルでOK(続行)を押した場合の処理(登録処理)
 */
const okAction = async (): Promise<void> => {
  if (state.confirmModalState.type === 'select') {
    await updateSelectedDate();
  } else {
    await updateRegisteredDate();
  }
  state.confirmModalState = { state: false, type: '' };
  await removeRecommendOfBusinesses();
  console.log('updated!');
  state.updateStatus = false; // 更新ボタンの非活性化
  await setUpAction();
};

/**
 * カレンダー登録モーダルでキャンセル(変更)を押した時の処理
 */
const cancelAction = (): void => {
  state.confirmModalState.state = false;
  state.confirmModalState = state.confirmModalState; // dataに定義そのものを再代入してvueにリアクティブにしてもらう
};

/**
 * 更新ボタンを押して、カレンダー登録モーダルを表示させる
 */
const onClickUpdateButton = (): void => {
  state.confirmModalState = { state: true, type: 'select' };
};

/**
 * カレンダーの日付部分のステータスを切り替える
 * @param data カレンダー情報
 */
const toggleDate = async (data: ICalendarData): Promise<void> => {
  state.reserveCalendar[data.date] = data; // acitve or isactiveが往復する可能性がある && 順不同なので
  state.updateStatus = true;
};

/**
 * 登録済みの日付の情報を更新する
 */
const updateRegisteredDate = async (): Promise<void> => {
  state.preRegisDate.workDays.sort(DateService.compareYearMonths);
  await putPreference(await getOrganizationId(), 'calendar', simpleCopy(state.preRegisDate)).catch((err) => console.log('err', err));
};

/**
 * 選択した日付の情報を更新する
 */
const updateSelectedDate = async (): Promise<void> => {
  /**
   * 稼働日の検索?(詳細不明)
   * @param a ''
   * @param b ''
   */
  const searchWorkDays = function (a: IBizDays, b: IBizDays) {
    // workdaysの中から最適なものを探す
    // 同一なら0
    // a < b なら1
    // a > b なら-1
    const aStrange = a.year * 1000 + a.month;
    const bStrange = b.year * 1000 + b.month;
    return aStrange - bStrange;
  };
  /**
   * ''
   * @param a ''
   * @param b ''
   */
  const searchDays = function (a: number, b: number) {
    // daysの中から最適なものを探す
    // 同一なら0
    // a < b なら1
    // a > b なら-1
    return a - b;
  };
  for (const key of Object.keys(state.reserveCalendar)) {
    const data: ICalendarData = state.reserveCalendar[key];
    const dateSplit = data.date.split('-');
    const targetYear = Number(dateSplit[0]);
    const targetMonth = Number(dateSplit[1]);
    const targetDay = Number(dateSplit[2]);
    const organizationId = data.organizationId;
    const binarySearched = binarySearch(state.activeCalendar.workDays, { days: [], year: targetYear, month: targetMonth }, searchWorkDays);
    let searchYearMonthIndex = binarySearched[0];
    if (searchYearMonthIndex == -1) {
      // 現状存在しなければ
      state.activeCalendar.workDays.splice(binarySearched[2], 0, {
        year: targetYear,
        month: targetMonth,
        days: [],
      });
      searchYearMonthIndex = binarySearched[2];
    }
    const searchDayAddIndex = binarySearch(state.activeCalendar.workDays[searchYearMonthIndex].days, targetDay, searchDays)[2];
    const searchDayIndex = binarySearch(state.activeCalendar.workDays[searchYearMonthIndex].days, targetDay, searchDays)[0];
    /*
        ここの削除と追加、
        速度とかアルゴリズム的な綺麗さを追求するなら、spliceがO(N)のことから、
        year,Monthのkeyごとに、まとめて処理を行う方が、断然綺麗だが、
        ここは、ループも現状遅くなりえないので、とりあえずこのままコードの読みやすさを追求した形に
        */
    if (!data.selected) {
      // 削除
      state.activeCalendar.workDays[searchYearMonthIndex].days.splice(searchDayIndex, 1);
    } else {
      // 追加
      state.activeCalendar.workDays[searchYearMonthIndex].days.splice(searchDayAddIndex, 0, targetDay);
    }
    state.activeCalendar.organizationId = organizationId;
    state.activeCalendar.updatedAt = Math.floor(new Date().getTime() / 1000);
    state.activeCalendar.unRegisterCalendarAlert = {
      updateAt: Math.floor(new Date().getTime() / 1000),
      isDisplay: true,
    };
  }
  state.activeCalendar.workDays.sort(DateService.compareYearMonths);
  await putPreference(await getOrganizationId(), 'calendar', simpleCopy(state.activeCalendar));
  await changeActiveCalendar(state.activeCalendar.workDays, state.activeCalendar.organizationId);
  state.reserveCalendar = {};
  removeChecker();
};

/**
 * カレンダー用のcsvファイルの読み取りを行う(と思われる)
 * @param e キャッチしたイベント？
 */
const calendarLoadCsvFile = async (e: any): Promise<void> => {
  const file = e.target.files[0];
  if (!file.name.match('.csv$')) {
    state.message = t('CSVファイルを選択してください');
    return;
  }
  const reader = new FileReader();
  reader.readAsText(file, 'UTF-8');
  reader.onload = async () => {
    state.lists = calenderConvert(reader.result);
    const copyWorkDays: IBizDays[] = Object.assign([], state.activeCalendar.workDays);

    const workDaysStrList = parseWorkDays(copyWorkDays);
    if (workDaysStrList.includes(`${state.lists[0].year}0${state.lists[0].month}`)) {
      const index = copyWorkDays.findIndex((day) => equalYearMonthInfo(day, state.lists[0]));
      copyWorkDays.splice(index, 12);
    }

    const calendar: ICalendar = {
      organizationId: await getOrganizationId(),
      updatedAt: Math.floor(new Date().getTime() / 1000),
      workDays: [...state.lists, ...copyWorkDays],
      unRegisterCalendarAlert: {
        updateAt: Math.floor(new Date().getTime() / 1000),
        isDisplay: true,
      },
    };
    state.preRegisDate = calendar;
    state.confirmModalState = { state: true, type: 'button' };
    return;
  };
};

/**
 * 稼働日のリストを作成する
 * @param workDays 稼働日情報の配列
 */
const parseWorkDays = (workDays: IBizDays[]): string[] => {
  const workDaysStrList: string[] = [];
  for (let i = 0; i < workDays.length; i++) {
    const month = String(workDays[i].month).length > 1 ? String(workDays[i].month) : `0${workDays[i].month}`;
    workDaysStrList.push(`${workDays[i].year}${month}`);
  }
  return workDaysStrList;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(FileInput, {
      ref_key: "inputCsvFile",
      ref: inputCsvFile,
      "data-testid": `input_file`,
      type: "file",
      class: "flex mb-1.5 max-w-xs",
      label: _unref(t)('稼働日csv取込'),
      onChange: calendarLoadCsvFile
    }, null, 8, ["label"]),
    _createVNode(ConfirmModal, {
      "modal-state": state.confirmModalState.state,
      "ok-button-text": _unref(t)('更新'),
      "cancel-button-text": _unref(t)('もどる'),
      onOk: okAction,
      onCancel: cancelAction,
      onOutsideClick: cancelAction
    }, {
      default: _withCtx(() => [
        _createVNode(ConfirmModalMain, { "data-testid": `confirm_modal_main` }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('カレンダーの登録情報を更新してもよろしいですか')), 1)
          ], undefined, true),
          _: 1
        })
      ], undefined, true),
      _: 1
    }, 8, ["modal-state", "ok-button-text", "cancel-button-text"]),
    _createElementVNode("p", null, _toDisplayString(state.message), 1),
    _createVNode(TextButton, {
      color: 'primary',
      size: 'btn-md',
      disabled: buttonDisabled.value,
      "data-testid": `update_button`,
      class: "flex mb-1.5",
      onClick: onClickUpdateButton
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)('カレンダーを更新')), 1)
      ], undefined, true),
      _: 1
    }, 8, ["disabled"]),
    _createVNode(YearCalendar, {
      modelValue: state.selectYear,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.selectYear) = $event)),
      "active-dates": state.activeDates,
      "onUpdate:activeDates": _cache[1] || (_cache[1] = ($event: any) => ((state.activeDates) = $event)),
      "organization-id": state.activeOrganizationId,
      "change-color": 'yellow',
      onToggleDate: toggleDate
    }, null, 8, ["modelValue", "active-dates", "organization-id"])
  ]))
}
}

})